'use client';

import { ReactNode } from 'react';
import { Box, Container, Flex, Heading, Icon, Text, VStack, HStack } from '@axon/ui';
import { motion } from 'framer-motion';
import { FiTruck, FiPackage, FiMapPin, FiShield } from 'react-icons/fi';

// Create motion component using direct wrapping
const MotionDiv = motion.div;

interface AuthLayoutProps {
  children: ReactNode;
}

interface FloatingIconProps {
  icon: React.ElementType;
  delay: number;
  top?: number | string;
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
  boxSize?: number | string;
}

const FloatingIcon = ({ icon, delay, ...props }: FloatingIconProps) => (
  <Box position="absolute" {...props}>
    <MotionDiv
      animate={{
        y: [0, -20, 0],
        rotate: [0, 10, -10, 0],
      }}
      transition={{
        duration: 6,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      <Icon asChild size="3xl" opacity={0.1} color="blue.600">
        <Box as={icon} />
      </Icon>
    </MotionDiv>
  </Box>
);

export default function AuthLayoutChakra({ children }: AuthLayoutProps) {
  const textColor = 'gray.600';

  return (
    <Box
      minHeight="100vh"
      background="linear-gradient(to bottom right, #EBF5FF, #FFFFFF, #FAF5FF)"
      position="relative"
      overflow="hidden"
      suppressHydrationWarning
    >
      {/* Background Pattern */}
      <Box
        position="absolute"
        inset={0}
        bgImage="radial-gradient(circle at 20% 80%, rgba(0, 102, 204, 0.1) 0%, transparent 50%),
                 radial-gradient(circle at 80% 20%, rgba(66, 133, 244, 0.1) 0%, transparent 50%),
                 radial-gradient(circle at 40% 40%, rgba(0, 33, 71, 0.05) 0%, transparent 50%)"
        pointerEvents="none"
        suppressHydrationWarning
      />

      {/* Floating Icons */}
      <FloatingIcon icon={FiTruck} delay={0} top={20} left={20} />
      <FloatingIcon icon={FiPackage} delay={2} top={40} right={32} boxSize={10} />
      <FloatingIcon icon={FiMapPin} delay={1} bottom={32} left={32} boxSize={14} />
      <FloatingIcon icon={FiShield} delay={3} bottom={20} right={20} boxSize={10} />

      <Container
        maxWidth="container.xl"
        height="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        paddingX={4}
      >
        <VStack gap={1} width="full" maxWidth="md" marginX="auto">
          {/* Logo Section */}
          <VStack gap={1} textAlign="center">
            <MotionDiv
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Flex
                width={24}
                height={24}
                backgroundColor="white"
                borderRadius="3xl"
                shadow="2xl"
                alignItems="center"
                justifyContent="center"
                position="relative"
                _hover={{ transform: 'scale(1.05)' }}
                transition="transform 0.2s"
                border="4px solid"
                borderColor="blue.600"
              >
                <Icon asChild size="3xl" color="blue.600">
                  <FiTruck />
                </Icon>
                <Box
                  position="absolute"
                  bottom={-2}
                  right={-2}
                  width={8}
                  height={8}
                  backgroundColor="blue.600"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                  fontWeight="bold"
                  color="white"
                >
                  TMS
                </Box>
              </Flex>
            </MotionDiv>

            <VStack gap={1}>
              <Heading
                size="2xl"
                fontWeight="bold"
                style={{
                  background: 'linear-gradient(to right, #0066CC, #7C3AED)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                TMS
              </Heading>
              <Text fontSize="lg" color={textColor} fontWeight="medium">
                Transportation Management System
              </Text>
              <Text fontSize="sm" color={textColor} opacity={0.8}>
                Streamline your logistics operations
              </Text>
            </VStack>
          </VStack>

          {/* Auth Form Container */}
          <Box width="full">
            <MotionDiv
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {children}
            </MotionDiv>
          </Box>

          {/* Footer */}
          <VStack gap={1} textAlign="center">
            <Text fontSize="sm" color={textColor}>
              © 2025 TMS. All rights reserved.
            </Text>
            <HStack gap={1} fontSize="xs" color={textColor} opacity={0.6}>
              <HStack>
                <Text>🚚</Text>
                <Text>Smart Logistics</Text>
              </HStack>
              <Text>•</Text>
              <HStack>
                <Text>📦</Text>
                <Text>Real-time Tracking</Text>
              </HStack>
              <Text>•</Text>
              <HStack>
                <Text>🛡️</Text>
                <Text>Secure Platform</Text>
              </HStack>
            </HStack>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
}
